import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useData } from '../context/DataContext';
import { coursesService } from '../services/firestoreService';
import { analyticsService } from '../services/analyticsService';
import SessionCard from '../components/shared/SessionCard';
import '../components/student/AvailableSessions.css';
import './PublicSessions.css';

const PublicSessions = () => {
  const { user } = useUser();
  const { sessions: allSessions, loading: dataLoading } = useData();
  const [sessionsWithCourses, setSessionsWithCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'upcoming'
  const [pageViews, setPageViews] = useState(0);
  const [shareMessage, setShareMessage] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [error, setError] = useState(null);
  const viewerIdRef = useRef(null);

  const ensureViewerId = useCallback(() => {
    if (user?.id) {
      viewerIdRef.current = user.id;
      return user.id;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const storageKey = 'lf_public_viewer_id';
    let storedId = window.localStorage.getItem(storageKey);
    if (!storedId) {
      const randomSegment = window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      storedId = `anon_${randomSegment}`;
      window.localStorage.setItem(storageKey, storedId);
    }
    viewerIdRef.current = storedId;
    return storedId;
  }, [user]);

  useEffect(() => {
    ensureViewerId();
    
    // Check for session parameter in URL for direct session sharing
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session');
    if (sessionId) {
      // Scroll to session after a short delay to allow content to load
      setTimeout(() => {
        const sessionElement = document.querySelector(`[data-session-id="${sessionId}"]`);
        if (sessionElement) {
          sessionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Highlight the session card
          sessionElement.style.boxShadow = '0 0 20px rgba(102, 126, 234, 0.5)';
          sessionElement.style.transition = 'box-shadow 0.3s ease';
          setTimeout(() => {
            sessionElement.style.boxShadow = '';
          }, 3000);
        }
      }, 500);
    }
  }, [ensureViewerId]);

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    return null;
  };

  const formatDateDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString() : 'TBD';
  };

  const buildSessionMeta = (session) => {
    const meta = [];
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (session.sessionCost != null && session.sessionCost !== '') {
      const costVal = typeof session.sessionCost === 'number' ? `$${session.sessionCost}` : session.sessionCost;
      meta.push({ icon: '💰', label: 'Cost', value: costVal });
    }
    return meta;
  };

  const buildCourseSection = (session) => {
    if (!session.course) return null;
    return {
      title: `📚 ${session.course.title}`,
      description: session.course.description,
      objectives: session.courseObjects ? <p>{session.courseObjects}</p> : null,
      expectations: session.expectations ? <p>{session.expectations}</p> : null
    };
  };

  const buildTopicEntries = (session) => {
    if (!session.course?.topics?.length) return [];
    return session.course.topics.map((topic, idx) => {
      const topicDatesFromSession = session.topicDates?.[topic.name] || [];
      const fallbackCourseDates = (!topicDatesFromSession.length && topic.date)
        ? [topic.date]
        : [];
      const resolvedTopicDates = topicDatesFromSession.length > 0
        ? topicDatesFromSession
        : fallbackCourseDates;
      return {
        id: `${session.id}-${idx}`,
        name: topic.name,
        hours: topic.hours ? `${topic.hours}h` : undefined,
        description: topic.description,
        dates: resolvedTopicDates.map((date) => ({
          label: `📅 ${formatDateDisplay(date)}`,
          type: 'default'
        }))
      };
    });
  };

  const renderCourseActions = (session, isArchived = false) => {
    if (!session.course) return null;
    return (
      <div className="public-session-actions">
        <Link 
          to={`/course/${session.course.id}`}
          className="view-course-btn"
        >
          📖 View Full Course
        </Link>
        <button 
          onClick={() => handleShare(session.course.id, session.course.title, session.id)}
          className="share-course-btn"
          title="Share this session"
        >
          📤 Share Session
        </button>
        {isArchived && (
          <span className="archived-badge" title="This session has ended">
            📦 Archived
          </span>
        )}
      </div>
    );
  };

  // Track page view and subscribe to count
  useEffect(() => {
    // Track the page view
    analyticsService.trackPageView('sessions-public');

    // Subscribe to page view count updates
    const unsubscribe = analyticsService.subscribeToPageViewCount('sessions-public', (count) => {
      setPageViews(count);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        console.log('PublicSessions: Loading sessions. Total sessions:', allSessions.length);
        // Fetch all sessions and enrich with course data
        const enrichedSessions = await Promise.all(
          allSessions.map(async (session) => {
            let course = null;
            if (session.courseId) {
              course = await coursesService.getCourse(session.courseId);
            }
            const enriched = {
              ...session,
              course
            };
            // Debug: Log session data to verify fields are present
            if (session.courseObjects || session.expectations || session.feesTerms) {
              console.log('PublicSessions: Session has custom fields:', {
                id: session.id,
                title: session.title,
                courseObjects: session.courseObjects,
                expectations: session.expectations,
                feesTerms: session.feesTerms
              });
            }
            return enriched;
          })
        );
        console.log('PublicSessions: Enriched sessions:', enrichedSessions.length);
        setSessionsWithCourses(enrichedSessions);
      } catch (error) {
        console.error('Error loading sessions:', error);
        setError('Failed to load sessions. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };

    if (!dataLoading && allSessions) {
      loadSessions();
    } else if (!dataLoading && allSessions.length === 0) {
      console.log('PublicSessions: No sessions available yet');
      setLoading(false);
    }
  }, [allSessions, dataLoading]);

  // Separate sessions into active/upcoming and archived (past)
  const { activeSessions, archivedSessions } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const active = [];
    const archived = [];

    sessionsWithCourses.forEach((session) => {
      const startDate = normalizeDateValue(session.startDate);
      const endDate = normalizeDateValue(session.endDate);

      if (!startDate || !endDate) return;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const isPast = endDate < today;

      if (isPast) {
        archived.push(session);
      } else {
        active.push(session);
      }
    });

    // Sort active by start date (earliest first)
    active.sort((a, b) => {
      const aStart = normalizeDateValue(a.startDate) || 0;
      const bStart = normalizeDateValue(b.startDate) || 0;
      return aStart - bStart;
    });

    // Sort archived by end date (most recent first)
    archived.sort((a, b) => {
      const aEnd = normalizeDateValue(a.endDate) || 0;
      const bEnd = normalizeDateValue(b.endDate) || 0;
      return bEnd - aEnd;
    });

    return { activeSessions: active, archivedSessions: archived };
  }, [sessionsWithCourses]);

  // Filter active sessions based on filter
  const filteredSessions = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return activeSessions.filter((session) => {
      const startDate = normalizeDateValue(session.startDate);
      const endDate = normalizeDateValue(session.endDate);

      if (!startDate || !endDate) return false;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const isActive = startDate <= today && endDate >= today;
      const isUpcoming = startDate > today;

      switch (filter) {
        case 'active':
          return isActive;
        case 'upcoming':
          return isUpcoming;
        case 'all':
        default:
          return true;
      }
    });
  }, [activeSessions, filter]);

  const handleShare = (courseId, courseName, sessionId = null) => {
    let shareUrl;
    if (sessionId) {
      // Session-level shareable link
      shareUrl = `${window.location.origin}/sessions?session=${sessionId}`;
    } else {
      // Course-level shareable link
      shareUrl = `${window.location.origin}/course/${courseId}`;
    }
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setShareMessage(`✅ ${sessionId ? 'Session' : 'Course'} link copied!`);
        setTimeout(() => setShareMessage(''), 3000);
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setShareMessage(`✅ ${sessionId ? 'Session' : 'Course'} link copied!`);
        setTimeout(() => setShareMessage(''), 3000);
      });
    } else {
      setShareMessage(`📋 Share URL: ${shareUrl}`);
      setTimeout(() => setShareMessage(''), 5000);
    }
  };

  useEffect(() => {
    if (!filteredSessions.length) return;
    if (typeof window === 'undefined') return;

    const viewerId = user?.id || viewerIdRef.current || ensureViewerId();
    filteredSessions.forEach((session) => {
      const storageKey = `lf_session_viewed_${session.id}`;
      if (!window.sessionStorage.getItem(storageKey)) {
        analyticsService.trackSessionView(session.id, session.courseId || null, viewerId);
        window.sessionStorage.setItem(storageKey, '1');
      }
    });
  }, [filteredSessions, user, ensureViewerId]);

  const getSessionStatus = (session) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = normalizeDateValue(session.startDate);
    const endDate = normalizeDateValue(session.endDate);

    if (!startDate || !endDate) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate <= today && endDate >= today) {
      return { label: '🟢 Active', type: 'active' };
    } else if (startDate > today) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    } else {
      return { label: '⚫ Past', type: 'past' };
    }
  };

  if (loading || dataLoading) {
    return (
      <div className="public-sessions-page">
        <div className="public-header">
          <div className="header-content">
            <h1>🎒 Learn for Fun</h1>
            <Link to="/" className="home-link">← Back to Home</Link>
          </div>
        </div>
        <div className="available-sessions">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading available sessions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-sessions-page">
        <div className="public-header">
          <div className="header-content">
            <h1>🎒 Learn for Fun</h1>
            <Link to="/" className="home-link">← Back to Home</Link>
          </div>
        </div>
        <div className="available-sessions">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Oops! Something went wrong</h3>
            <p>{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="retry-btn"
            >
              🔄 Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-sessions-page">
      <div className="public-header">
        <div className="header-content">
          <h1>🎒 Learn for Fun</h1>
          <Link to="/" className="home-link">← Back to Home</Link>
        </div>
      </div>
      <div className="available-sessions">
        <div className="sessions-header-row">
          <h1>📅 Available Sessions</h1>
          {user && user.type === 'admin' && (
            <div className="page-views">👁️ {pageViews.toLocaleString()} views</div>
          )}
        </div>
        <div className="page-header">
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All Available
            </button>
            <button
              className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
              onClick={() => setFilter('active')}
            >
              🟢 Active
            </button>
            <button
              className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              🔵 Upcoming
            </button>
          </div>
        </div>

        <div className="signup-prompt">
          <p>Want to enroll in these sessions? <Link to="/">Sign up</Link> or <Link to="/">Sign in</Link> to get started!</p>
        </div>

        {shareMessage && <div className="share-notification">{shareMessage}</div>}

        {/* Active/Upcoming Sessions */}
        {filteredSessions.length === 0 ? (
          <div className="empty-state">
            <p>No {filter === 'all' ? 'available' : filter} sessions found.</p>
          </div>
        ) : (
          <div className="sessions-grid">
            {filteredSessions.map((session) => (
              <div key={session.id} data-session-id={session.id}>
                <SessionCard
                  title={session.title}
                  status={getSessionStatus(session)}
                  meta={buildSessionMeta(session)}
                  course={buildCourseSection(session)}
                  topics={buildTopicEntries(session)}
                  actions={renderCourseActions(session, false)}
                >
                {session.feesTerms && (
                  <div className="fees-terms">
                    <strong>💰 Fees/Terms:</strong>
                    <p>{session.feesTerms}</p>
                  </div>
                )}
              </SessionCard>
              </div>
            ))}
          </div>
        )}

        {/* Archived Sessions Section */}
        {archivedSessions.length > 0 && (
          <div className="archived-sessions-section">
            <div className="archived-header">
              <h2>📦 Archived Sessions</h2>
              <button
                className={`toggle-archived-btn ${showArchived ? 'expanded' : ''}`}
                onClick={() => setShowArchived(!showArchived)}
                aria-expanded={showArchived}
              >
                {showArchived ? '▼ Hide' : '▶ Show'} ({archivedSessions.length})
              </button>
            </div>
            {showArchived && (
              <div className="archived-sessions-grid">
                {archivedSessions.map((session) => (
                  <div key={session.id} data-session-id={session.id}>
                    <SessionCard
                      title={session.title}
                      status={{ label: '⚫ Past', type: 'past' }}
                      meta={buildSessionMeta(session)}
                      course={buildCourseSection(session)}
                      topics={buildTopicEntries(session)}
                      actions={renderCourseActions(session, true)}
                      className="archived-session-card"
                    >
                    {session.feesTerms && (
                      <div className="fees-terms">
                        <strong>💰 Fees/Terms:</strong>
                        <p>{session.feesTerms}</p>
                      </div>
                    )}
                    <div className="archived-notice">
                      <p>⚠️ Registration is closed for this session as it has ended.</p>
                    </div>
                  </SessionCard>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicSessions;

